/**
 * Extract custom HTML legend items from elements inside the same
 * [data-pdf-chart-group] container as the chart wrapper.
 * Looks for elements matching a common legend pattern:
 *   .loan-purpose-legend__item (or similar) with __dot, __label, __value children.
 */
function captureLegendItems(wrapper) {
  const group = wrapper.closest('[data-pdf-chart-group]');
  if (!group) {
    return [];
  }

  const legendItems = group.querySelectorAll('[class*="legend__item"]');
  if (!legendItems.length) {
    return [];
  }

  return [...legendItems]
    .map(item => {
      const dot = item.querySelector('[class*="legend__dot"]');
      const label = item.querySelector('[class*="legend__label"]');
      const value = item.querySelector('[class*="legend__value"]');
      return {
        color: dot ? getComputedStyle(dot).backgroundColor : null,
        label: label?.textContent?.trim() || '',
        value: value?.textContent?.trim() || '',
      };
    })
    .filter(item => item.label);
}

/**
 * Capture companion KPI(s) that live in the same [data-pdf-chart-group]
 * container as the chart wrapper.
 */
function scrapeKpiElements(kpiEls) {
  return [...kpiEls]
    .map(el => ({
      label: el.querySelector('[data-pdf-kpi-label]')?.textContent?.trim() || '',
      value: directTextContent(el.querySelector('[data-pdf-kpi-value]')),
      sub: el.querySelector('[data-pdf-kpi-sub]')?.textContent?.trim() || '',
      style: el.dataset.pdfKpi || '',
    }))
    .filter(kpi => kpi.label || kpi.value);
}

function captureCompanionKpis(wrapper) {
  const group = wrapper.closest('[data-pdf-chart-group]');
  if (!group) {
    return [];
  }
  const kpiEls = group.querySelectorAll('[data-pdf-kpi]');
  return scrapeKpiElements(kpiEls);
}

/**
 * Capture all chart canvases within a container element.
 * Charts are identified by the `data-pdf-chart` attribute on their wrapper.
 * Returns an array of { title, subtitle, image, width, height, legend, companionKpis } objects.
 */
export function captureCharts(containerEl) {
  if (!containerEl || containerEl.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const wrappers = containerEl.querySelectorAll('[data-pdf-chart]');
  return [...wrappers]
    .map(wrapper => {
      const canvas = wrapper.querySelector('canvas');
      let image = null;
      if (canvas) {
        try {
          image = canvas.toDataURL('image/png');
        } catch (e) {
          // Tolerate per-chart failures (SecurityError, canvas-size limits, QuotaExceededError).
          // Filter below drops the chart so the rest of the export still succeeds.
          console.error('[PDF] canvas capture failed, skipping chart', e);
        }
      }
      return {
        title: wrapper.dataset.pdfChartTitle || '',
        subtitle: wrapper.dataset.pdfChartSubtitle || '',
        image,
        width: canvas ? canvas.width : 0,
        height: canvas ? canvas.height : 0,
        legend: captureLegendItems(wrapper),
        companionKpis: captureCompanionKpis(wrapper),
        layout: wrapper.dataset.pdfChartLayout || '',
      };
    })
    .filter(chart => chart.image);
}

/**
 * Get direct text content of an element, excluding child element text.
 */
function directTextContent(el) {
  if (!el) {
    return '';
  }
  let text = '';
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    }
  }
  return text.trim();
}

/**
 * Read KPI values from annotated DOM elements.
 *
 * Templates should not author the `data-pdf-*` attributes directly. Use the
 * wrappers that own the contract:
 *   - `<pdf-chart title subtitle layout>` for chart wrappers
 *   - `v-pdf-chart-group` directive for the surrounding chart-group container
 *   - `<app-stat pdf-kpi>` for KPI cells (AppStat renders the data-pdf-kpi-*
 *     attributes when `pdfKpi` is set)
 *
 * The DOM contract those wrappers produce is:
 *   [data-pdf-chart-group]            chart-group container
 *   [data-pdf-chart]                  chart wrapper
 *   [data-pdf-chart-title]            chart title
 *   [data-pdf-chart-subtitle]         chart subtitle
 *   [data-pdf-chart-layout]           "triple" | "" (default)
 *   [data-pdf-kpi]                    KPI element ("teal" | "orange" | "pink" | "")
 *     [data-pdf-kpi-label]            inner: KPI label
 *     [data-pdf-kpi-value]            inner: KPI value
 *     [data-pdf-kpi-sub]              inner: KPI sublabel
 */
export function captureKpis(containerEl) {
  if (!containerEl || containerEl.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const kpiEls = containerEl.querySelectorAll('[data-pdf-kpi]');
  return scrapeKpiElements(kpiEls);
}

/**
 * Capture KPI elements whose enclosing chart-group has no chart (or that
 * aren't in any chart-group at all). These are "loose" stat cards that the
 * inline layout would otherwise drop, because companion-KPI capture only
 * picks up KPIs paired with a captured chart.
 */
export function captureOrphanKpis(containerEl) {
  if (!containerEl || containerEl.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const kpiEls = containerEl.querySelectorAll('[data-pdf-kpi]');
  const orphans = [...kpiEls].filter(el => {
    const group = el.closest('[data-pdf-chart-group]');
    if (!group) {
      return true;
    }
    return !group.querySelector('[data-pdf-chart]');
  });
  return scrapeKpiElements(orphans);
}
